import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Clock, AlertTriangle, CheckCircle, Calendar, FileText, Loader2 } from 'lucide-react';
import api, { documentsApi, medicalVisitsApi, certificatesApi } from '../lib/api';

export default function ScadenzePage() {
  const [loading, setLoading] = useState(true);
  const [scadenze, setScadenze] = useState({
    documents: [],
    medicalVisits: [],
    certificates: [],
    trainings: []
  });

  useEffect(() => {
    loadScadenze();
  }, []);

  const loadScadenze = async () => {
    try {
      const [docsRes, medicalRes, certsRes] = await Promise.all([
        documentsApi.getAll(),
        medicalVisitsApi.getExpiring(90),
        certificatesApi.getAll()
      ]);

      const today = new Date();
      const ninetyDaysFromNow = new Date();
      ninetyDaysFromNow.setDate(ninetyDaysFromNow.getDate() + 90);

      // Filter expiring documents
      const expiringDocs = (docsRes.data || []).filter(doc => {
        if (!doc.expiry_date) return false;
        const expiry = new Date(doc.expiry_date);
        return expiry <= ninetyDaysFromNow;
      }).map(doc => ({
        ...doc,
        type: 'document',
        typeLabel: 'Documento',
        icon: FileText
      }));

      // Filter expiring medical visits
      const expiringMedical = (medicalRes.data || []).map(visit => ({
        ...visit,
        expiry_date: visit.next_visit_date,
        name: `Visita medica - ${visit.personnel_name || 'N/A'}`,
        type: 'medical',
        typeLabel: 'Visita Medica',
        icon: Calendar
      }));

      // Filter expiring certificates
      const expiringCerts = (certsRes.data || []).filter(cert => {
        if (!cert.expiry_date) return false;
        const expiry = new Date(cert.expiry_date);
        return expiry <= ninetyDaysFromNow;
      }).map(cert => ({
        ...cert,
        name: cert.course_name,
        type: 'certificate',
        typeLabel: 'Attestato',
        icon: CheckCircle
      }));

      setScadenze({
        documents: expiringDocs,
        medicalVisits: expiringMedical,
        certificates: expiringCerts,
        trainings: []
      });
    } catch (error) {
      toast.error('Errore nel caricamento delle scadenze');
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryBadge = (days) => {
    if (days === null) return null;
    if (days < 0) return <Badge variant="destructive">Scaduto</Badge>;
    if (days <= 7) return <Badge variant="destructive">{days}g</Badge>;
    if (days <= 30) return <Badge variant="warning" className="bg-orange-500">{days}g</Badge>;
    return <Badge variant="secondary">{days}g</Badge>;
  };

  const allScadenze = [
    ...scadenze.documents,
    ...scadenze.medicalVisits,
    ...scadenze.certificates
  ].sort((a, b) => {
    const daysA = getDaysUntilExpiry(a.expiry_date);
    const daysB = getDaysUntilExpiry(b.expiry_date);
    return (daysA || 999) - (daysB || 999);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const urgentCount = allScadenze.filter(s => {
    const days = getDaysUntilExpiry(s.expiry_date);
    return days !== null && days <= 7;
  }).length;

  const warningCount = allScadenze.filter(s => {
    const days = getDaysUntilExpiry(s.expiry_date);
    return days !== null && days > 7 && days <= 30;
  }).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Clock className="w-6 h-6" />
          Scadenze
        </h1>
        <p className="text-muted-foreground">Monitoraggio scadenze documenti, visite e attestati</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={urgentCount > 0 ? 'border-red-500' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Urgenti (entro 7gg)</p>
                <p className="text-3xl font-bold text-red-600">{urgentCount}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card className={warningCount > 0 ? 'border-orange-500' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In scadenza (entro 30gg)</p>
                <p className="text-3xl font-bold text-orange-600">{warningCount}</p>
              </div>
              <Clock className="w-10 h-10 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale monitorato</p>
                <p className="text-3xl font-bold">{allScadenze.length}</p>
              </div>
              <Calendar className="w-10 h-10 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scadenze List */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Scadenze</CardTitle>
        </CardHeader>
        <CardContent>
          {allScadenze.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <p>Nessuna scadenza nei prossimi 90 giorni</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allScadenze.map((item, index) => {
                const days = getDaysUntilExpiry(item.expiry_date);
                const IconComponent = item.icon || FileText;
                return (
                  <div 
                    key={`${item.type}-${item.id || index}`}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      days !== null && days < 0 ? 'bg-red-50 border-red-200' :
                      days !== null && days <= 7 ? 'bg-red-50 border-red-200' :
                      days !== null && days <= 30 ? 'bg-orange-50 border-orange-200' :
                      'bg-background'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <IconComponent className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.typeLabel} • Scade: {item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                        </p>
                      </div>
                    </div>
                    {getExpiryBadge(days)}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
